import React, { useState } from 'react';

const todayIso = () => new Date().toLocaleDateString('en-CA');
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

/**
 * Record a payment (or mark fully paid) against a challan.
 * Props: row (ChallanPaymentRow), onClose(), onSubmit(payload) -> {success,error}
 */
export default function RecordPaymentModal({ row, onClose, onSubmit }) {
  const outstanding = Number(row?.outstanding || 0);
  const [markFull, setMarkFull] = useState(true);
  const [amount, setAmount] = useState(String(outstanding));
  const [mode, setMode] = useState('bank');
  const [paidOn, setPaidOn] = useState(todayIso());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const payload = {
      mark_full: markFull,
      amount: markFull ? undefined : Number(amount),
      paid_on: paidOn || undefined,
      mode: mode || undefined,
      reference: reference || undefined,
      note: note || undefined,
    };
    if (!markFull && (!(Number(amount) > 0))) { setErr('Enter a valid amount'); return; }
    if (!markFull && Number(amount) > outstanding) { setErr(`Amount exceeds outstanding (₹ ${fmt(outstanding)})`); return; }
    setSubmitting(true);
    const res = await onSubmit(payload);
    setSubmitting(false);
    if (res && res.success === false) { setErr(res.error || 'Failed to record payment'); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Record Payment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          <div className="flex justify-between bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-gray-500">Challan</span>
            <span className="font-semibold">{row?.challan_number}</span>
          </div>
          <div className="flex justify-between px-3">
            <span className="text-gray-500">Outstanding</span>
            <span className="font-bold text-red-600">₹ {fmt(outstanding)}</span>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={markFull} onChange={(e) => setMarkFull(e.target.checked)} />
            <span>Mark fully paid (₹ {fmt(outstanding)})</span>
          </label>
          {!markFull && (
            <div>
              <label className="block font-medium text-gray-600 mb-1">Amount</label>
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-medium text-gray-600 mb-1">Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white">
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-600 mb-1">Date</label>
              <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
                     className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
            </div>
          </div>
          <div>
            <label className="block font-medium text-gray-600 mb-1">Reference (txn / cheque no.)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
          </div>
          <div>
            <label className="block font-medium text-gray-600 mb-1">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-gray-300" />
          </div>
          {err && <p className="text-red-600 text-xs">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border-2 border-gray-300">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold disabled:opacity-60">
              {submitting ? 'Saving…' : 'Save Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
